"use client";

/**
 * The contact page's side column, one card (owner, 2026-10-26: "join the
 * right side and your conversations into one component, so there is a
 * bit less crowding"): the account's conversations first when there are
 * any, then the other ways to reach us, then the pages that answer the
 * usual questions. On phones the card comes before the form when the
 * account has conversations (a person who wrote looks for the answer).
 */

import { AtSign, ChevronRight, Mail } from "lucide-react";
import { LINK } from "@/lib/ui-classes";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";

const CONTACT_EMAIL = "hello@menzil.ai";

const QUICK: { href: string; key: TranslationKey }[] = [
  { href: "/faq", key: "contact.aside.faq" },
  { href: "/methodology", key: "contact.aside.method" },
  { href: "/pricing", key: "contact.aside.pricing" },
];

export default function ContactAside({ conversations, first = false }: { conversations?: React.ReactNode; first?: boolean }) {
  const t = useT();
  return (
    <aside className={`space-y-4 ${first ? "order-first lg:order-none" : ""}`}>
      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        {conversations && <div className="mb-5 border-b border-gray-100 pb-5">{conversations}</div>}
        <h2 className="text-sm font-bold text-gray-900">{t("contact.aside.reach")}</h2>
        <ul className="mt-3 space-y-2.5 text-sm">
          <li className="flex items-center gap-2.5">
            <Mail className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
            <a href={`mailto:${CONTACT_EMAIL}`} className={LINK}>{CONTACT_EMAIL}</a>
          </li>
          <li className="flex items-center gap-2.5">
            <AtSign className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
            <a href="https://www.instagram.com/menzil.ai/" target="_blank" rel="noopener noreferrer" className={LINK}>Instagram</a>
          </li>
        </ul>
        <h2 className="mt-5 border-t border-gray-100 pt-5 text-sm font-bold text-gray-900">{t("contact.aside.quick")}</h2>
        <ul className="mt-1 divide-y divide-gray-100">
          {QUICK.map((q) => (
            <li key={q.href}>
              <Link href={q.href} className="flex items-center justify-between gap-2 py-2.5 text-sm font-medium text-gray-800 hover:text-gray-900">
                {t(q.key)}<ChevronRight className="h-4 w-4 text-gray-400" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <p className="px-1 text-sm leading-relaxed text-gray-600">{t("contact.aside.tip")}</p>
    </aside>
  );
}
